import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { ReactComponent as UserIcon } from '@/assets/icons/user-plus.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import BackboneUserBadge from '@/components/BackboneUserBadge';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import InvoicesWarning from '@/pages/InvoiceList/components/InvoicesWarning';
import {
  FreelancerDepartmentDetailsResource,
  FreelancerDetailsResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { FreelancerStatus } from '@/utils/enums';
import { calculateAge, renderCurrencySign } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import FreelancerFormModal from './FreelancerFormModal';
import WorkEmailFormModal from './WorkEmailFormModal';

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    &:last-child {
      cursor: initial;
    }
  }
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
      &:last-child {
        background-color: ${({ theme }) => theme.colors.white} !important;
      }
    }
  }
`;

const StatusHolder = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentHolder = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const NoWrapTd = styled(Td)`
  white-space: nowrap;
`;

const StatusDot = styled.div<{ $status?: FreelancerStatus }>`
  width: 4px;
  height: 4px;
  background: ${({ theme }) => theme.colors.error};
  border-radius: 50%;
  margin-right: 6px;
  transform: translateY(1px);
  ${({ $status }) =>
    $status === FreelancerStatus.EMPLOYED &&
    css`
      background: ${({ theme }) => theme.colors.success};
    `}
  ${({ $status }) =>
    $status === FreelancerStatus.UNEMPLOYED &&
    css`
      background: ${({ theme }) => theme.colors.error};
    `}
`;

const ContentDot = styled.div`
  width: 4px;
  height: 4px;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 50%;
  margin-right: 6px;
  transform: translateY(1px);
`;

const BorderTd = styled(Td)`
  position: relative;
`;

const ReceiverWrapper = styled.div`
  display: flex;
  white-space: nowrap;
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 8px;
`;

const LeftBorder = styled.span<{ $isError?: boolean }>`
  background: ${({ theme }) => theme.colors.warning};
  border-radius: 0px 2px 2px 0px;
  width: 3px;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  ${({ $isError }) =>
    $isError &&
    css`
      background: ${({ theme }) => theme.colors.error};
    `}
`;

const GrayText = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  margin-top: 2px;
`;

function isDepartmentEndingWithinLastTwoWeeks(
  department: FreelancerDepartmentDetailsResource,
  referenceDate: DateTime,
) {
  if (!department.endedAt) {
    return false;
  }

  const endedAtDate = DateTime.fromISO(department.endedAt);
  const daysSinceEnded = referenceDate.diff(endedAtDate, 'days').days;

  const TWO_WEEKS = 14;
  return daysSinceEnded >= -TWO_WEEKS && daysSinceEnded <= 1;
}

interface Props {
  freelancer: FreelancerDetailsResource;
  index: number;
  sortExpiringContracts: boolean;
  userCanOnlyManageWorkEmail: boolean;
}

const FreelancersTableRow: FC<Props> = ({
  freelancer,
  index,
  sortExpiringContracts,
  userCanOnlyManageWorkEmail,
}) => {
  const currentDate = DateTime.now();

  const userCanSeeData = useHasPermission('view all freelancers');
  const userCanManageData = useHasPermission('manage freelancers');
  const userCanManageWorkEmail = useHasPermission(
    'manage freelancer work email',
  );

  const filteredDepartments = freelancer.departmentDetails?.filter(
    (department) =>
      isDepartmentEndingWithinLastTwoWeeks(department, currentDate),
  );
  const { type } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const [showFreelancerModal, setShowFreelancerModal] = useState(false);
  const [showWorkEmailModal, setShowWorkEmailModal] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews['freelancersTable'],
  );

  const handleRowClick = (id: number) => {
    navigate(`/general/freelancers/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this freelancer?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/freelancers/${freelancer.id}`,
      );

      await queryClient.invalidateQueries(['freelancers_details'], {
        exact: false,
      });

      alerts.success('Freelancer was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const handleAddAsUser = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!freelancer.workEmail) {
      alerts.error('Freelancer has no work email');
      return;
    }
    setIsCreatingUser(true);
    try {
      await api.post('/v2/users', {
        email: freelancer.workEmail,
        role: 'Team Member',
        assign_to_all_teams: false,
        teams: [],
        permissions: [],
        production_teams: [],
        assign_to_all_production_teams: false,
      });
      await queryClient.invalidateQueries(['freelancers_details'], {
        exact: false,
      });
      alerts.success('User successfully created');
    } catch (e) {
      alerts.error();
    } finally {
      setIsCreatingUser(false);
    }
  };

  const filteredDetails =
    type !== 'all' && type !== 'good' && !sortExpiringContracts
      ? freelancer.departmentDetails?.filter(
          (detail) => detail.status?.toLowerCase() === type,
        )
      : freelancer.departmentDetails;

  const filteredWage = (
    details: FreelancerDepartmentDetailsResource,
    type: string,
  ) => {
    // The backend sends wages newest first.
    const currentWage = details.wages?.find((wage) => wage.type === type);

    if (!currentWage) {
      return '-';
    }

    if (type === 'other') {
      return currentWage.text_amount ?? '-';
    }

    return `${currentWage.amount} ${!!currentWage.currency && renderCurrencySign(currentWage.currency!)}`;
  };

  return (
    <>
      <Tr
        onClick={
          userCanOnlyManageWorkEmail
            ? undefined
            : () => handleRowClick(freelancer.id)
        }
      >
        <BorderTd>
          {!userCanOnlyManageWorkEmail && !freelancer.are_fields_prefilled && (
            <LeftBorder />
          )}
          {!!filteredDepartments?.length && <LeftBorder $isError />}
          {index}.
        </BorderTd>
        <Td>
          <ReceiverWrapper>
            <Badges>
              {!userCanOnlyManageWorkEmail &&
                !freelancer.are_fields_prefilled && (
                  <InvoicesWarning isFreelancer />
                )}
              {freelancer.isUser && <BackboneUserBadge />}
            </Badges>
            <Text $type="smallHeadline">{freelancer.member.name} </Text>
          </ReceiverWrapper>
        </Td>
        {userCanSeeData && (
          <Td>
            {DateTime.fromISO(freelancer.createdAt).toFormat('yyyy-LL-dd')}
          </Td>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions['Start Date'] && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot />{' '}
                    {department.startedAt
                      ? DateTime.fromISO(department.startedAt).toFormat(
                          'yyyy-LL-dd',
                        )
                      : '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <StatusHolder key={department.id}>
                    <StatusDot $status={department.status} />{' '}
                    {department.status ?? '-'}
                  </StatusHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {userCanSeeData && tableViewOptions.Category && (
          <Td>{freelancer.category ?? '-'}</Td>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions.Company && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot />{' '}
                    {department.companies.map((c) => c.name).join(', ') || '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions.Department && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot /> {department.department?.name ?? '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions.Subunit && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot /> {department.subunit?.name ?? '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions.Role && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot /> {department.role?.name ?? '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {userCanSeeData && tableViewOptions['Finance Account Number'] && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot />{' '}
                    {department.financeAccountNumber?.name ?? '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions['Employment Type'] && (
          <NoWrapTd>
            {filteredDetails?.length
              ? filteredDetails.map((department) => (
                  <ContentHolder key={department.id}>
                    <ContentDot /> {department.employmentType ?? '-'}
                  </ContentHolder>
                ))
              : '-'}
          </NoWrapTd>
        )}
        {!userCanOnlyManageWorkEmail &&
          tableViewOptions['Current Hour Wage'] && (
            <NoWrapTd>
              {filteredDetails?.length
                ? filteredDetails.map((department) => (
                    <ContentHolder key={department.id}>
                      <ContentDot /> {filteredWage(department, 'hour')}
                    </ContentHolder>
                  ))
                : '-'}
            </NoWrapTd>
          )}
        {!userCanOnlyManageWorkEmail &&
          tableViewOptions['Current Day Wage'] && (
            <NoWrapTd>
              {filteredDetails?.length
                ? filteredDetails.map((department) => (
                    <ContentHolder key={department.id}>
                      <ContentDot /> {filteredWage(department, 'day')}
                    </ContentHolder>
                  ))
                : '-'}
            </NoWrapTd>
          )}
        {!userCanOnlyManageWorkEmail &&
          tableViewOptions['Current Month Wage'] && (
            <NoWrapTd>
              {filteredDetails?.length
                ? filteredDetails.map((department) => (
                    <ContentHolder key={department.id}>
                      <ContentDot /> {filteredWage(department, 'month')}
                    </ContentHolder>
                  ))
                : '-'}
            </NoWrapTd>
          )}
        {!userCanOnlyManageWorkEmail &&
          tableViewOptions['Current Single Job Wage'] && (
            <NoWrapTd>
              {filteredDetails?.length
                ? filteredDetails.map((department) => (
                    <ContentHolder key={department.id}>
                      <ContentDot /> {filteredWage(department, 'job')}
                    </ContentHolder>
                  ))
                : '-'}
            </NoWrapTd>
          )}
        {!userCanOnlyManageWorkEmail &&
          tableViewOptions['Current Other Wage'] && (
            <NoWrapTd>
              {filteredDetails?.length
                ? filteredDetails.map((department) => (
                    <ContentHolder key={department.id}>
                      <ContentDot /> {filteredWage(department, 'other')}
                    </ContentHolder>
                  ))
                : '-'}
            </NoWrapTd>
          )}
        {(userCanSeeData || userCanManageWorkEmail) &&
          tableViewOptions['Work Email'] && (
            <Td>{freelancer.workEmail ?? '-'}</Td>
          )}
        {userCanSeeData && tableViewOptions['Personal Code'] && (
          <Td>{freelancer.personalCode ?? '-'}</Td>
        )}
        {!userCanOnlyManageWorkEmail && tableViewOptions['Gender'] && (
          <Td>{freelancer.gender ?? '-'}</Td>
        )}
        {userCanSeeData && tableViewOptions['Birthday Date'] && (
          <Td>
            {freelancer.dateOfBirth ? (
              <>
                {DateTime.fromISO(freelancer.dateOfBirth).toFormat(
                  'yyyy-LL-dd',
                )}{' '}
                <br />
                <GrayText>
                  {calculateAge(freelancer.dateOfBirth)} years old
                </GrayText>
              </>
            ) : (
              '-'
            )}
          </Td>
        )}
        {userCanSeeData && tableViewOptions['Phone Number'] && (
          <Td>{freelancer.phoneNumber ?? '-'}</Td>
        )}
        {(userCanSeeData || userCanManageWorkEmail) &&
          tableViewOptions['Personal Email'] && (
            <Td>{freelancer.personalEmail ?? '-'}</Td>
          )}
        {userCanSeeData && tableViewOptions.Address && (
          <Td>{freelancer.member.address ?? '-'}</Td>
        )}
        {userCanSeeData && tableViewOptions['Created By'] && (
          <Td>{freelancer.creator ?? '-'}</Td>
        )}
        {userCanSeeData && tableViewOptions['Last Edited'] && (
          <Td>{freelancer.edited_by ?? '-'}</Td>
        )}
        {(userCanManageData || userCanManageWorkEmail) && (
          <Td $actionsCol onClick={(e) => e.stopPropagation()}>
            <ActionsWrap>
              <ActionsDropdown>
                {userCanManageData && (
                  <>
                    <ActionsDropdownButton
                      as="button"
                      onClick={() => setShowFreelancerModal(true)}
                    >
                      <span>
                        <PencilIcon />
                      </span>
                      Edit
                    </ActionsDropdownButton>
                    {!freelancer.isUser && (
                      <ActionsDropdownButton
                        as="button"
                        onClick={handleAddAsUser}
                        disabled={isCreatingUser}
                      >
                        <span>
                          <UserIcon />
                        </span>
                        Add as a Backbone user
                      </ActionsDropdownButton>
                    )}
                    <ActionsDropdownButton as="button" onClick={onDelete}>
                      <span>
                        <TrashIcon />
                      </span>
                      Delete
                    </ActionsDropdownButton>
                  </>
                )}
                {userCanManageWorkEmail && !userCanManageData && (
                  <>
                    {!freelancer.isUser && (
                      <ActionsDropdownButton
                        as="button"
                        onClick={handleAddAsUser}
                        disabled={isCreatingUser}
                      >
                        <span>
                          <UserIcon />
                        </span>
                        Add as a Backbone user
                      </ActionsDropdownButton>
                    )}
                    <ActionsDropdownButton
                      as="button"
                      onClick={() => setShowWorkEmailModal(true)}
                    >
                      <span>
                        <PencilIcon />
                      </span>
                      Edit Work Email
                    </ActionsDropdownButton>
                  </>
                )}
              </ActionsDropdown>
            </ActionsWrap>
          </Td>
        )}
      </Tr>
      <AnimatePresence>
        {userCanManageData && showFreelancerModal && (
          <FreelancerFormModal
            freelancer={freelancer}
            onClose={() => {
              setShowFreelancerModal(false);
            }}
          />
        )}
        {userCanManageWorkEmail && showWorkEmailModal && (
          <WorkEmailFormModal
            freelancer={freelancer}
            onClose={() => setShowWorkEmailModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancersTableRow;
